import  { useState, useEffect, useMemo } from 'react';
import { Search, ArrowUpDown, RefreshCw, Layers, ChevronRight, X } from 'lucide-react';
import { API_URL } from '../../trip/utils/fatch';
import { Link } from 'react-router-dom';
import styles from './tags.module.css';

export const TagAdminPage = () => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name_en', direction: 'asc' });
  const [selectedTag, setSelectedTag] = useState(null); // Stores the tag object
  const [details, setDetails] = useState({ parents: [], children: [] });
  const [detailsLoading, setDetailsLoading] = useState(false);

  // 1. Fetch data from your API
  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    setLoading(true);
    try {
      const response = await fetch(API_URL + '/admin/tags',
         {
                    method: 'GET', // Use 'POST' for login
                    credentials: 'include', // ✅ Required for cookies

                }
      );
      const data = await response.json();
      setTags(data);
    } catch (error) {
      console.error("Failed to fetch tags:", error);
    } finally {
      setLoading(false);
    }
  };

    const handleTagClick = async (tag) => {
    setSelectedTag(tag);
    setDetailsLoading(true);
    try {
      const res = await fetch(API_URL +`/admin/tags/${tag.viator_tag_id}/details`,
            {
                    method: 'GET', // Use 'POST' for login
                    credentials: 'include', // ✅ Required for cookies

                }
      );
      const data = await res.json();
      setDetails(data);
    } catch (error) {
      console.error("Error fetching details", error);
    } finally {
      setDetailsLoading(false);
    }
  };

  // 2. Logic: Search and Sort (useMemo for performance with 1,200+ items)
  const processedTags = useMemo(() => {
    let filtered = tags.filter(tag => 
      tag.name_en.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tag.viator_tag_id.toString().includes(searchTerm)
    );

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
        if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }, [tags, searchTerm, sortConfig]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  return (
     <div className="relative flex min-h-screen bg-gray-50 overflow-hidden">
         
   {/*   <div className="p-6 max-w-6xl mx-auto bg-gray-50 min-h-screen"> */ }

   <div className={`flex-1 p-6 transition-all duration-300 ${selectedTag ? 'mr-96' : ''}`}>
    
      <div className="flex justify-between items-center mb-6">
         <Link to="/admin" className={styles.adminLink}>Go Back</Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Viator Category Manager</h1>
          <p className="text-gray-500 text-sm">{tags.length} Total Categories Synced</p>
        </div>
        <button 
          onClick={fetchTags}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
        >
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          Refresh List
        </button>
      </div>

      {/* Search Bar */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Search by name or Viator ID..."
          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto max-h-[700px]">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th onClick={() => requestSort('viator_tag_id')} className="p-4 cursor-pointer hover:bg-gray-100 text-gray-600 font-semibold text-sm">
                  Viator ID <ArrowUpDown size={14} className="inline ml-1" />
                </th>
                <th onClick={() => requestSort('name_en')} className="p-4 cursor-pointer hover:bg-gray-100 text-gray-600 font-semibold text-sm">
                  Category Name <ArrowUpDown size={14} className="inline ml-1" />
                </th>
                <th className="p-4 text-gray-600 font-semibold text-sm">Hierarchy</th>
                <th className="p-4 text-gray-600 font-semibold text-sm">Last Synced</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan="4" className="p-10 text-center text-gray-400">Loading categories...</td></tr>
              ) : processedTags.length > 0 ? (
                processedTags.map((tag) => (
                  <tr key={tag.id} className={`hover:bg-blue-50/50 transition ${selectedTag?.viator_tag_id === tag.viator_tag_id ? 'bg-blue-50' : ''}`}   onClick={() => handleTagClick(tag)}>
                    <td className="p-4 font-mono text-sm text-gray-500">{tag.viator_tag_id}</td>
                    <td className="p-4 font-medium text-gray-800">{tag.name_en}</td>
                    <td className="p-4">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-md text-xs">
                        <Layers size={12} />
                        {tag.parent_count} Parents
                      </span>
                    </td>
                    <td className="p-4 text-xs text-gray-400">
                      {new Date(tag.updated_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="4" className="p-10 text-center text-gray-400">No categories found matching &ldquo;{searchTerm}&ldquo;</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
       <aside className={`fixed right-0 top-0 h-full bg-white shadow-2xl border-l w-96 transform transition-transform duration-300 ease-in-out z-12 ${selectedTag ? 'translate-x-0' : 'translate-x-full'}`}>
        {selectedTag && (
          <div className="flex flex-col h-full">
            <div className="p-4 border-b flex justify-between items-center bg-gray-50">
              <h2 className="font-bold text-lg truncate">{selectedTag.name_en}</h2>
              <button onClick={() => setSelectedTag(null)} className="p-1 hover:bg-gray-200 rounded">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <section className="mb-8">
                <h3 className="text-xs font-bold uppercase text-gray-400 tracking-widest mb-4">Parent Categories</h3>
                {detailsLoading ? <div className="animate-pulse h-4 bg-gray-100 rounded mb-2 w-full"></div> : 
                  details.parents.length > 0 ? details.parents.map(p => (
                    <div key={p.viator_tag_id} className="flex items-center gap-2 p-2 bg-blue-50 text-blue-700 rounded-lg mb-2 text-sm">
                      <Layers size={14} /> {p.name_en}
                    </div>
                  )) : <p className="text-gray-400 text-sm">No parents (Top-level)</p>
                }
              </section>

              <section>
                <h3 className="text-xs font-bold uppercase text-gray-400 tracking-widest mb-4">Child Categories</h3>
                {detailsLoading ? <div className="animate-pulse h-4 bg-gray-100 rounded mb-2 w-full"></div> : 
                  details.children.length > 0 ? details.children.map(c => (
                    <button 
                      key={c.viator_tag_id} 
                      onClick={() => handleTagClick(c)}
                      className="w-full flex justify-between items-center p-3 border rounded-lg mb-2 hover:border-blue-500 text-sm group"
                    >
                      {c.name_en}
                      <ChevronRight size={14} className="text-gray-300 group-hover:text-blue-500" />
                    </button>
                  )) : <p className="text-gray-400 text-sm">No sub-categories</p>
                }
              </section>
            </div>
          </div>
        )}
      </aside>
        </div>
  );
};












